import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
  setNotifications, 
  addMoreNotifications, 
  setLoading, 
  setError, 
  setFetchError,
  setFetchingMore,
  incrementPageNumber,
  clearErrors,
  updateUnreadCount
} from "../Slices/NotificationsSlice";
import { callApi } from "../common/Apicall";

// How long an already-loaded notification list is considered fresh enough to
// reuse across route changes.
export const NOTIFICATIONS_STALE_MS = 60 * 1000;

export const useNotifications = () => {
  const dispatch = useDispatch();
  const [isFetching, setIsFetching] = useState(false);
  const URL = process.env.REACT_APP_URL;
  
  const { pageNumber, pageSize, hasMore, notifications, lastFetchedAt } =
    useSelector((state) => state.notifications);

  const fetchNotifications = useCallback(async (isLoadMore = false) => {
    if (isLoadMore && (!hasMore || isFetching)) return;
    
    // Clear previous errors
    dispatch(clearErrors());
    
    if (isLoadMore) {
      dispatch(setFetchingMore(true));
    } else {
      setIsFetching(true);
      dispatch(setLoading(true));
    }

    try {
      const targetPage = isLoadMore ? pageNumber + 1 : 1;
      const res = await callApi(
        `${URL}/Notification/GetUserNotifications?pageNumber=${targetPage}&pageSize=${pageSize}`,
        "GET"
      );

      if (res) {
        // For load more, we need to combine existing notifications with new ones
        // and calculate the unread count from all notifications
        const allNotifications = isLoadMore 
          ? [...notifications, ...(res.notifications || [])]
          : res.notifications || [];
        
        const unreadCount = allNotifications.filter(n => !n.isRead).length;
        
        const payload = {
          notifications: res.notifications || [],
          unreadCount,
          totalCount: res.totalCount || 0,
          totalPages: res.totalPages || 1,
          pageNumber: res.pageNumber || targetPage
        };

        if (isLoadMore) {
          dispatch(addMoreNotifications(payload));
          dispatch(incrementPageNumber());
        } else {
          dispatch(setNotifications(payload));
        }
        
        return res.notifications || [];
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch notifications";
      
      dispatch(setFetchError(errorMessage));
      dispatch(setError(errorMessage));
      
      throw error;
    } finally {
      if (isLoadMore) {
        dispatch(setFetchingMore(false));
      } else {
        setIsFetching(false);
        dispatch(setLoading(false));
      }
    }
  }, [URL, dispatch, pageNumber, pageSize, hasMore, isFetching, notifications]);

  // The Header is rendered by every screen, so it mounts again on each route
  // change and used to refetch the notification list every time. This only
  // goes to the API when the list has never been loaded or has gone stale,
  // which keeps the unread badge current without the per-navigation storm.
  const fetchNotificationsIfStale = useCallback(
    (maxAgeMs = NOTIFICATIONS_STALE_MS) => {
      if (lastFetchedAt && Date.now() - lastFetchedAt < maxAgeMs) return;
      if (isFetching) return;
      return fetchNotifications();
    },
    [lastFetchedAt, isFetching, fetchNotifications],
  );

  const loadMoreNotifications = useCallback(() => {
    if (hasMore && !isFetching) {
      return fetchNotifications(true);
    }
  }, [hasMore, isFetching, fetchNotifications]);

  // Function to manually update unread count (useful after batch operations)
  const refreshUnreadCount = useCallback(() => {
    dispatch(updateUnreadCount());
  }, [dispatch]);

  return {
    fetchNotifications,
    fetchNotificationsIfStale,
    loadMoreNotifications,
    refreshUnreadCount,
    isFetching,
    hasMore,
  };
};